import { useEffect } from "react";

/**
 * Supported orientation lock types for Screen Orientation API
 */
type OrientationLockType =
  | "portrait"
  | "landscape"
  | "portrait-primary"
  | "portrait-secondary"
  | "landscape-primary"
  | "landscape-secondary"
  | "any"
  | "natural";

/**
 * Extended ScreenOrientation interface with lock/unlock methods
 */
interface ScreenOrientationExtended extends ScreenOrientation {
  lock: (orientation: OrientationLockType) => Promise<void>;
  unlock: () => void;
}

/**
 * Lock screen orientation for the current page
 *
 * Uses the Screen Orientation API to lock the device orientation.
 * - Works on mobile browsers and PWAs
 * - Requires fullscreen mode on desktop browsers (often not available)
 * - Gracefully falls back if not supported
 *
 * @param orientation - 'portrait' | 'landscape' | 'portrait-primary' | 'landscape-primary' | etc.
 * @param enabled - Whether to apply the lock (default: true, useful for conditional locks)
 *
 * @example
 * ```tsx
 * // Lock to portrait on Home page
 * useOrientationLock('portrait');
 *
 * // Lock to landscape on Player page
 * useOrientationLock('landscape');
 *
 * // Conditional lock (only on mobile devices)
 * const isMobile = DEVICE.isTouchOptimized(width, height);
 * useOrientationLock('portrait', isMobile);
 * ```
 */
export function useOrientationLock(orientation: OrientationLockType, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;

    // Check if Screen Orientation API is available
    const screenOrientation = screen?.orientation as ScreenOrientationExtended | undefined;
    if (!screenOrientation?.lock) {
      console.debug("[useOrientationLock] Screen Orientation API not supported");
      return;
    }

    // Attempt to lock orientation
    const lockPromise = screenOrientation.lock(orientation);

    lockPromise
      .then(() => {
        console.debug(`[useOrientationLock] Locked to ${orientation}`);
      })
      .catch((error: Error) => {
        // Common reasons for failure:
        // - Not in fullscreen mode (desktop)
        // - Permission denied
        // - Browser doesn't support this orientation
        // Silently ignore in development - this is expected on desktop browsers
        if (import.meta.env.PROD) {
          console.warn(`[useOrientationLock] Failed to lock orientation: ${error.message}`);
        }
      });

    // Cleanup: unlock orientation when component unmounts
    return () => {
      if (screenOrientation?.unlock) {
        screenOrientation.unlock();
        console.debug("[useOrientationLock] Unlocked orientation");
      }
    };
  }, [orientation, enabled]);
}
